import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { 
  Users, 
  Target, 
  Phone, 
  Mail, 
  Search,
  Filter,
  ArrowRight,
  RefreshCw,
  Eye,
  BarChart3,
  Clock,
  Loader2,
  CheckCircle2,
  XCircle,
  TrendingUp,
  Calendar
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  prospect: "bg-slate-100 text-slate-700",
  contacted: "bg-blue-100 text-blue-700",
  qualified: "bg-purple-100 text-purple-700",
  closed_won: "bg-emerald-100 text-emerald-700",
  closed_lost: "bg-red-100 text-red-700"
};

const statusLabels = {
  prospect: "Prospect",
  contacted: "Contacted",
  qualified: "Qualified",
  closed_won: "Won",
  closed_lost: "Lost"
};

export default function ManagerOversightPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [teamLeads, setTeamLeads] = useState([]);
  const [teamActivity, setTeamActivity] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedRep, setSelectedRep] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [newRepId, setNewRepId] = useState("");
  const [repStatsDialogOpen, setRepStatsDialogOpen] = useState(false);
  const [selectedRepStats, setSelectedRepStats] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [selectedRep, selectedStatus]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedRep !== "all") params.append("rep_id", selectedRep);
      if (selectedStatus !== "all") params.append("status", selectedStatus);

      const [leadsRes, activityRes, membersRes] = await Promise.all([
        fetch(`${API}/manager/team-leads?${params}`, { credentials: "include" }),
        fetch(`${API}/manager/team-activity?days=7`, { credentials: "include" }),
        fetch(`${API}/users`, { credentials: "include" })
      ]);

      if (leadsRes.ok) {
        const data = await leadsRes.json();
        setTeamLeads(data.leads || []);
      }
      if (activityRes.ok) {
        const data = await activityRes.json();
        setTeamActivity(data.activity || []);
      }
      if (membersRes.ok) {
        const data = await membersRes.json();
        setTeamMembers(Array.isArray(data) ? data : data.users || []);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRepStats = async (repId) => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/manager/rep-stats/${repId}`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedRepStats(data);
        setRepStatsDialogOpen(true);
      }
    } catch (error) {
      toast.error("Failed to load rep stats");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSelectLead = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLeads.length === filteredLeads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(filteredLeads.map(l => l.lead_id));
    }
  };

  const handleReassign = async () => {
    if (!newRepId || selectedLeads.length === 0) {
      toast.error("Please select leads and a new rep");
      return;
    }

    setActionLoading(true);
    try {
      const response = await fetch(`${API}/manager/bulk-reassign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          lead_ids: selectedLeads,
          new_rep_id: newRepId
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Reassigned ${data.count} leads to ${data.new_rep_name}`);
        setSelectedLeads([]);
        setReassignDialogOpen(false);
        setNewRepId("");
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to reassign leads");
      }
    } catch (error) {
      toast.error("Failed to reassign leads");
    } finally {
      setActionLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit"
    });
  };

  const filteredLeads = teamLeads.filter(lead => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return lead.business_name?.toLowerCase().includes(query) ||
             lead.email?.toLowerCase().includes(query) ||
             lead.phone?.includes(query);
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="manager-oversight-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
            Team Oversight
          </h1>
          <p className="text-slate-500 mt-1">
            Monitor and manage your sales team&apos;s leads and activity
          </p>
        </div>
        <Button onClick={fetchData} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Team Member Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {teamMembers.slice(0, 6).map((member) => (
          <Card 
            key={member.user_id}
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => fetchRepStats(member.user_id)}
          >
            <CardContent className="pt-4 text-center">
              <Avatar className="h-12 w-12 mx-auto mb-2">
                <AvatarImage src={member.picture} />
                <AvatarFallback>{getInitials(member.name)}</AvatarFallback>
              </Avatar>
              <p className="font-medium text-sm truncate">{member.name}</p>
              <p className="text-xs text-slate-500 truncate">{member.email}</p>
              <Button size="sm" variant="ghost" className="mt-2 text-xs">
                <Eye className="w-3 h-3 mr-1" />
                View Stats
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="leads" className="space-y-4">
        <TabsList>
          <TabsTrigger value="leads">
            <Target className="w-4 h-4 mr-2" />
            Team Leads ({filteredLeads.length})
          </TabsTrigger>
          <TabsTrigger value="activity">
            <Clock className="w-4 h-4 mr-2" />
            Recent Activity
          </TabsTrigger>
        </TabsList>

        {/* Team Leads Tab */}
        <TabsContent value="leads">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Team Leads</CardTitle>
                  <CardDescription>View and manage all leads assigned to your team</CardDescription>
                </div>
                {selectedLeads.length > 0 && (
                  <Button onClick={() => setReassignDialogOpen(true)}>
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Reassign {selectedLeads.length} Lead{selectedLeads.length > 1 ? "s" : ""}
                  </Button>
                )}
              </div>
              
              {/* Filters */}
              <div className="flex gap-4 mt-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search leads..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={selectedRep} onValueChange={setSelectedRep}>
                  <SelectTrigger className="w-48">
                    <Users className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter by rep" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Team Members</SelectItem>
                    {teamMembers.map((member) => (
                      <SelectItem key={member.user_id} value={member.user_id}>
                        {member.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="w-40">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {Object.entries(statusLabels).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedLeads.length === filteredLeads.length && filteredLeads.length > 0}
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Business</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead>Last Contact</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeads.slice(0, 50).map((lead) => (
                    <TableRow key={lead.lead_id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedLeads.includes(lead.lead_id)}
                          onCheckedChange={() => handleSelectLead(lead.lead_id)}
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{lead.business_name}</p>
                          <p className="text-xs text-slate-500">{lead.city}, {lead.state}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {lead.phone && <p>{lead.phone}</p>}
                          {lead.email && <p className="text-slate-500 text-xs">{lead.email}</p>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[lead.status]}>
                          {statusLabels[lead.status]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm">{lead.assigned_to_name || "Unassigned"}</p>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-slate-500">
                          {lead.last_contacted_at ? formatDate(lead.last_contacted_at) : "Never"}
                        </p>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => navigate(`/leads/${lead.lead_id}`)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {filteredLeads.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <Target className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No leads found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity">
          <Card>
            <CardHeader>
              <CardTitle>Recent Team Activity</CardTitle>
              <CardDescription>Calls and emails from the last 7 days</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {teamActivity.map((activity, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={activity.user_picture} />
                      <AvatarFallback>{getInitials(activity.user_name)}</AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{activity.user_name}</span>
                        <Badge variant="outline" className="text-xs">
                          {activity.type === "call" ? (
                            <><Phone className="w-3 h-3 mr-1" /> Call</>
                          ) : (
                            <><Mail className="w-3 h-3 mr-1" /> Email</>
                          )}
                        </Badge>
                        {activity.type === "call" && activity.outcome && (
                          <Badge className={
                            activity.outcome === "answered" ? "bg-emerald-100 text-emerald-700" :
                            activity.outcome === "no_answer" ? "bg-slate-100 text-slate-700" :
                            "bg-blue-100 text-blue-700"
                          }>
                            {activity.outcome}
                          </Badge>
                        )}
                      </div>
                      
                      <p className="text-sm mt-1">
                        {activity.type === "call" 
                          ? `Called ${activity.lead_name}`
                          : `Sent "${activity.subject}" to ${activity.lead_name}`
                        }
                      </p>
                      
                      {activity.notes && (
                        <p className="text-sm text-slate-500 mt-1">{activity.notes}</p>
                      )}
                      
                      <p className="text-xs text-slate-400 mt-2">
                        {formatDate(activity.timestamp)}
                      </p>
                    </div>
                    
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => navigate(`/leads/${activity.lead_id}`)}
                    >
                      View Lead
                    </Button>
                  </div>
                ))}
                
                {teamActivity.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <Clock className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>No recent activity</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Reassign Dialog */}
      <Dialog open={reassignDialogOpen} onOpenChange={setReassignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reassign Leads</DialogTitle>
            <DialogDescription>
              Select a team member to reassign {selectedLeads.length} lead{selectedLeads.length > 1 ? "s" : ""} to
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Select value={newRepId} onValueChange={setNewRepId}>
              <SelectTrigger>
                <SelectValue placeholder="Select team member" />
              </SelectTrigger>
              <SelectContent>
                {teamMembers.map((member) => (
                  <SelectItem key={member.user_id} value={member.user_id}>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={member.picture} />
                        <AvatarFallback className="text-xs">{getInitials(member.name)}</AvatarFallback>
                      </Avatar>
                      {member.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleReassign} disabled={actionLoading || !newRepId}>
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              Reassign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rep Stats Dialog */}
      <Dialog open={repStatsDialogOpen} onOpenChange={setRepStatsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedRepStats?.rep && (
                <>
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={selectedRepStats.rep.picture} />
                    <AvatarFallback>{getInitials(selectedRepStats.rep.name)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p>{selectedRepStats.rep.name}</p>
                    <p className="text-sm text-slate-500 font-normal">{selectedRepStats.rep.email}</p>
                  </div>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedRepStats && (
            <div className="space-y-6 py-4">
              {/* Lead Stats */}
              <div>
                <h4 className="font-medium text-sm text-slate-500 mb-3">Lead Performance</h4>
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-slate-700">{selectedRepStats.leads.total}</p>
                    <p className="text-xs text-slate-500">Total Leads</p>
                  </div>
                  <div className="text-center p-3 bg-emerald-50 rounded-lg">
                    <p className="text-2xl font-bold text-emerald-600">{selectedRepStats.leads.won}</p>
                    <p className="text-xs text-slate-500">Won</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <p className="text-2xl font-bold text-red-600">{selectedRepStats.leads.lost}</p>
                    <p className="text-xs text-slate-500">Lost</p>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{selectedRepStats.leads.conversion_rate}%</p>
                    <p className="text-xs text-slate-500">Win Rate</p>
                  </div>
                </div>
              </div>
              
              {/* Call Stats */}
              <div>
                <h4 className="font-medium text-sm text-slate-500 mb-3">Call Activity</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold">{selectedRepStats.calls.total}</p>
                    <p className="text-xs text-slate-500">Total Calls</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold">{selectedRepStats.calls.this_week}</p>
                    <p className="text-xs text-slate-500">This Week</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold">{selectedRepStats.calls.this_month}</p>
                    <p className="text-xs text-slate-500">This Month</p>
                  </div>
                </div>
                
                {/* Call Outcomes */}
                {Object.keys(selectedRepStats.calls.outcomes || {}).length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {Object.entries(selectedRepStats.calls.outcomes).map(([outcome, count]) => (
                      <Badge key={outcome} variant="outline">
                        {outcome}: {count}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Email Stats */}
              <div>
                <h4 className="font-medium text-sm text-slate-500 mb-3">Email Activity</h4>
                <div className="p-3 bg-slate-50 rounded-lg inline-block">
                  <p className="text-2xl font-bold">{selectedRepStats.emails.total}</p>
                  <p className="text-xs text-slate-500">Emails Sent</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
